import React, { useCallback, useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Superuser-only: monitor for the DAILY auto-fetch job (daily_channel_refresh) — checks every
// watched channel for new videos in a short window and downloads them. Download-only (no extraction).
const GOLD = 'rgb(var(--brand-gold-rgb))';

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const statusPill = (st) => ({
  running: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}[st] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300');

export default function AutoFetchPanel() {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(null);   // run id whose per-channel breakdown is expanded

  const load = useCallback(async () => {
    try { setRuns((await researchosService.getDailyRefresh()).data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Poll while a refresh is running.
  useEffect(() => {
    if (!runs.some((r) => r.status === 'running')) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [runs, load]);

  const runNow = async () => {
    setBusy(true); setMsg('');
    try {
      await researchosService.triggerDailyRefresh();
      setMsg('Refresh dispatched — it will appear below shortly.');
      setTimeout(load, 1500);
    } catch (e) {
      setMsg('Failed to dispatch: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const latest = runs[0];
  const windowDays = latest?.stats?.window_days ?? 7;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Runs daily at 6:00 AM ET — checks every watched channel for new videos in the last{' '}
          <span className="text-gray-700 dark:text-gray-200">{windowDays} days</span> and downloads them
          (transcripts only; extraction stays manual).
        </p>
        <button onClick={runNow} disabled={busy}
          className="ml-auto rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
          {busy ? 'Dispatching…' : 'Run now'}
        </button>
      </div>
      {msg && <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{msg}</p>}

      {/* Latest run headline stats */}
      {latest && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ['New videos', latest.stats?.total_new ?? 0, '#5fbf8f'],
            ['Channels checked', latest.stats?.channel_count ?? (latest.stats?.channels || []).length, null],
            ['Failed', latest.stats?.total_failed ?? 0, (latest.stats?.total_failed ?? 0) > 0 ? '#e0705f' : null],
            ['No-transcript', latest.stats?.total_no_transcript ?? 0, null],
          ].map(([label, val, color]) => (
            <div key={label} className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
              <div className="font-serif text-2xl tabular-nums" style={{ color: color || undefined }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Run history with per-channel breakdown */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-100 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 dark:border-gray-800/60">
          Auto-fetch history
        </div>
        {runs.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No auto-fetch has run yet. It fires daily, or press “Run now”.</p>
        ) : runs.map((r) => {
          const s = r.stats || {};
          const chans = (s.channels || []).filter((c) => (c.new || 0) > 0 || c.error || c.skipped || (c.failed || 0) > 0);
          const isOpen = open === r.id;
          return (
            <div key={r.id} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800/60">
              <button onClick={() => setOpen(isOpen ? null : r.id)}
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${statusPill(r.status)}`}>{r.status}</span>
                <span className="tabular-nums text-green-600 dark:text-green-400">{s.total_new ?? 0} new</span>
                <span className="tabular-nums text-gray-500">{s.channel_count ?? (s.channels || []).length} channels · {s.window_days ?? 7}d</span>
                {(s.total_failed ?? 0) > 0 && <span className="tabular-nums text-red-600 dark:text-red-400">{s.total_failed} failed</span>}
                <span className="ml-auto text-xs text-gray-400">{fmtTime(r.started_at)}</span>
              </button>
              {isOpen && (
                <div className="bg-gray-50 px-4 py-2 dark:bg-gray-800/30">
                  {chans.length === 0 ? (
                    <p className="text-xs text-gray-500">No new videos this run.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400">
                          <th className="py-1">Channel</th><th className="py-1 text-right">New</th>
                          <th className="py-1 text-right">Had</th><th className="py-1 text-right">Failed</th>
                          <th className="py-1 text-right">No-tx</th><th className="py-1">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chans.map((c, i) => (
                          <tr key={i} className="border-t border-gray-100 dark:border-gray-800/60">
                            <td className="py-1 text-gray-700 dark:text-gray-200">{c.channel}</td>
                            <td className="py-1 text-right tabular-nums text-green-600 dark:text-green-400">{c.new ?? 0}</td>
                            <td className="py-1 text-right tabular-nums text-gray-500">{c.had ?? 0}</td>
                            <td className="py-1 text-right tabular-nums text-red-500">{c.failed ?? 0}</td>
                            <td className="py-1 text-right tabular-nums text-gray-400">{c.no_transcript ?? 0}</td>
                            <td className="py-1 text-gray-400">{c.error || c.skipped || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
